import React, { useState } from "react";
import { CheckIcon } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { Button } from "@/components/ui/button";
import { OnboardingStep } from "../utils/getOnboardingSteps";
import { ProviderOnboardingStep } from "../enums/ProviderOnboardingStep";
import { ProviderUser } from "@/common/types/ProviderUser";

export default function OnboardingStepper({
  stepIndex,
  onboardingSteps,
  providerUser,
  onNavigateToStep,
}: {
  stepIndex: number;
  onboardingSteps: OnboardingStep[];
  providerUser?: ProviderUser;
  onNavigateToStep?: (stepId: ProviderOnboardingStep) => void;
}) {
  const [openTooltips, setOpenTooltips] = useState<string[]>([]);

  // Check if a step is completed using the step's valid function
  const isStepCompleted = (step: OnboardingStep): boolean => {
    return providerUser ? step.valid(providerUser) : false;
  };

  return (
    <div className="pl-5">
      <ol className="hidden lg:block relative text-gray-500 border-s border-gray-200 dark:border-gray-700 dark:text-gray-400">
        {onboardingSteps.map((step, index) => {
          const isCompleted = isStepCompleted(step);
          const isCurrent = stepIndex === index;
          const isClickable = onNavigateToStep != null;

          let bgClassName =
            "absolute flex items-center justify-center w-10 h-10 rounded-full -start-5";
          const iconClassName = "w-4 h-4 text-white";

          if (isCompleted) {
            bgClassName += " bg-green-700";
          } else if (isCurrent) {
            bgClassName += " bg-primary";
          } else {
            bgClassName += " bg-slate-400";
          }

          if (isClickable) {
            bgClassName +=
              " cursor-pointer hover:ring-2 hover:ring-offset-2 hover:ring-primary transition-all";
          }

          const Icon = step.icon;

          const handleClick = () => {
            if (onNavigateToStep) {
              onNavigateToStep(step.id);
            }
          };

          return (
            <li key={step.title} className="not-last:mb-10 ms-8">
              <button
                type="button"
                onClick={handleClick}
                disabled={!isClickable}
                className={bgClassName}
                aria-label={`Navigate to ${step.title}`}
                aria-current={isCurrent ? "step" : undefined}
              >
                {isCompleted ? (
                  <CheckIcon className={iconClassName} />
                ) : (
                  <Icon className={iconClassName} />
                )}
              </button>
              <h3
                className={`font-medium leading-9 ${isClickable ? "cursor-pointer" : ""}`}
                onClick={handleClick}
              >
                {step.title}
              </h3>
            </li>
          );
        })}
      </ol>
      <ol className="flex items-center w-full lg:hidden">
        {onboardingSteps.map((step, index) => {
          const isCompleted = isStepCompleted(step);
          const isCurrent = stepIndex === index;
          const isClickable = onNavigateToStep != null;

          let bgClassName =
            "flex items-center justify-center w-10 h-10 rounded-full shrink-0";
          const iconClassName = "w-3.5 h-3.5 text-white";

          if (isCompleted) {
            bgClassName += " bg-green-700";
          } else if (isCurrent) {
            bgClassName += " bg-primary";
          } else {
            bgClassName += " bg-slate-400";
          }

          if (isClickable) {
            bgClassName +=
              " hover:ring-2 hover:ring-offset-2 hover:ring-primary transition-all";
          }

          const onOpenChange = (open: boolean) => {
            setOpenTooltips((prevState) => {
              if (open) {
                if (openTooltips.includes(step.title)) {
                  return prevState;
                } else {
                  return prevState.concat([step.title]);
                }
              } else {
                return prevState.filter((item) => item !== step.title);
              }
            });
          };
          const Icon = step.icon;

          const handleClick = () => {
            if (onNavigateToStep) {
              onNavigateToStep(step.id);
            }
          };

          return (
            <li
              key={step.title}
              className={`flex items-center w-full dark:text-blue-500 ${index === onboardingSteps.length - 1 ? "" : "after:content-[''] after:w-full after:h-1 after:border-b after:border-slate-300 after:border-4 after:inline-block dark:after:border-blue-800"}`}
            >
              <TooltipProvider>
                <Tooltip
                  delayDuration={300}
                  open={openTooltips.includes(step.title)}
                  onOpenChange={onOpenChange}
                >
                  <TooltipTrigger asChild>
                    <Button
                      variant="none"
                      size="none"
                      onClick={() => {
                        if (isClickable) {
                          handleClick();
                        } else {
                          onOpenChange(true);
                        }
                      }}
                      className={bgClassName}
                      aria-label={`Navigate to ${step.title}`}
                      aria-current={isCurrent ? "step" : undefined}
                    >
                      {isCompleted ? (
                        <CheckIcon className={iconClassName} />
                      ) : (
                        <Icon className={iconClassName} />
                      )}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    {step.title}
                    {isClickable && " (tap to navigate)"}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
